import React from 'react';

interface PieceItem {
  slug: string;
  title?: string;
  lifecycleState: string;
  renditions: Record<string, any>;
}

interface QualityDashboardProps {
  brand: string;
  pieces: PieceItem[];
  onSelectPiece: (slug: string) => void;
}

function humanTitle(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// #20 fix: Use worst (min) score consistently across all views
function bestScore(renditions: Record<string, any>): { score: number | null; passed: boolean | null } {
  let worst: number | null = null;
  let allPassed: boolean | null = null;
  for (const r of Object.values(renditions || {})) {
    if (r.score != null) worst = worst === null ? r.score : Math.min(worst, r.score);
    if (r.passed != null) allPassed = allPassed === null ? r.passed : allPassed && r.passed;
  }
  return { score: worst, passed: allPassed };
}

export default function QualityDashboard({ brand, pieces, onSelectPiece }: QualityDashboardProps) {
  const scored = pieces.filter((p) => bestScore(p.renditions).score !== null);
  const avgScore = scored.length > 0 ? Math.round(scored.reduce((s, p) => s + (bestScore(p.renditions).score || 0), 0) / scored.length) : null;
  const passedCount = scored.filter((p) => bestScore(p.renditions).passed).length;
  const needsWork = pieces.filter((p) => {
    const { score, passed } = bestScore(p.renditions);
    return (score !== null && !passed) || (Object.keys(p.renditions || {}).length === 0 && ['draft', 'review'].includes(p.lifecycleState));
  });

  return (
    <div className="view-page">
      <h2 className="view-page__title">Quality Overview</h2>

      <div className="quality-stats">
        <div className="quality-stat">
          <div className="quality-stat__value" style={{ color: avgScore && avgScore >= 80 ? 'var(--success)' : 'var(--warning)' }}>{avgScore ?? '\u2014'}</div>
          <div className="quality-stat__label">Avg Score</div>
        </div>
        <div className="quality-stat">
          <div className="quality-stat__value" style={{ color: 'var(--success)' }}>{passedCount}/{scored.length}</div>
          <div className="quality-stat__label">Passed</div>
        </div>
        <div className="quality-stat">
          <div className="quality-stat__value" style={{ color: needsWork.length > 0 ? 'var(--warning)' : 'var(--text-tertiary)' }}>{needsWork.length}</div>
          <div className="quality-stat__label">Needs Work</div>
        </div>
      </div>

      {needsWork.length > 0 && (
        <div className="quality-section">
          <h3 className="quality-section__title">Needs Attention</h3>
          {needsWork.map((p) => {
            const { score } = bestScore(p.renditions);
            return (
              <button key={p.slug} className="quality-item" onClick={() => onSelectPiece(p.slug)}>
                <span className="quality-item__title">{p.title || humanTitle(p.slug)}</span>
                <span className="quality-item__score">{score !== null ? `${score} \u2717` : 'No score'}</span>
              </button>
            );
          })}
        </div>
      )}

      {scored.length === 0 && (
        <div style={{ color: 'var(--text-tertiary)', padding: 20, textAlign: 'center' }}>
          No scored content yet. Run pipelines to generate content and quality scores.
        </div>
      )}
    </div>
  );
}
